<template>
  <b-card>
    <b-form @submit.prevent>
      <!-- Dados do comunicado -->
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Título do comunicado" label-for="title">
            <b-form-input
              id="title"
              v-model="title"
              placeholder="Ex.: Novidades de Julho"
              :class="{ 'is-invalid': v$.title.$error }"
            />
            <div class="invalid-feedback">
              <span v-if="v$.title.required.$invalid">
                Você deve informar um título
              </span>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Versão (opcional)" label-for="version">
            <b-form-input id="version" v-model="version" placeholder="Ex.: 2.14.0" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Status" label-for="status">
            <v-select
              v-model="status"
              :options="statusOptions"
              :reduce="(option) => option.value"
              label="name"
              :clearable="false"
              dir="ltr"
            >
              <template #no-options> Sem resultados </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>

      <hr />

      <!-- Itens do carrossel -->
      <div class="d-flex align-items-center justify-content-between mb-1">
        <h5 class="mb-0">Itens do carrossel</h5>
        <b-button variant="outline-primary" size="sm" @click="addItem">
          <feather-icon icon="PlusCircleIcon" class="mr-50" />
          Adicionar item
        </b-button>
      </div>

      <div v-if="v$.items.$error" class="text-danger mb-1">
        <small v-if="publishing">
          Para publicar, cada item precisa de título, descrição e um vídeo ou imagem.
        </small>
        <small v-else>Adicione ao menos um item com título.</small>
      </div>

      <b-card
        v-for="(item, index) in items"
        :key="index"
        border-variant="secondary"
        class="mb-1"
      >
        <div class="d-flex align-items-center justify-content-between mb-1">
          <b-badge variant="light-primary">Item {{ index + 1 }}</b-badge>
          <div>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :disabled="index === 0"
              @click="moveItem(index, -1)"
            >
              <feather-icon icon="ArrowUpIcon" />
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :disabled="index === items.length - 1"
              @click="moveItem(index, 1)"
            >
              <feather-icon icon="ArrowDownIcon" />
            </b-button>
            <b-button
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              @click="removeItem(index)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </div>
        <b-row>
          <b-col cols="12" md="8">
            <b-form-group label="Título do item">
              <b-form-input
                v-model="item.title"
                placeholder="Ex.: Novo dashboard"
                :class="{ 'is-invalid': itemTitleInvalid(index) }"
              />
              <div class="invalid-feedback">
                <span>Informe o título do item</span>
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Categoria">
              <v-select
                v-model="item.category"
                :options="categories"
                dir="ltr"
              >
                <template #no-options> Sem resultados </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Vídeo (formato MP4, até 20MB)">
              <div v-if="item.video_src" class="mb-50">
                <video
                  :src="item.video_src"
                  controls
                  class="whats-new-video-preview"
                />
              </div>
              <b-form-file
                :key="item.fileKey"
                v-model="item.videoFile"
                accept="video/mp4,video/webm,video/quicktime"
                browse-text="Selecionar"
                :placeholder="item.video_url ? 'Trocar vídeo...' : 'Escolha um vídeo...'"
                :disabled="item.uploading"
                @input="onVideoSelected(item, $event)"
              />
              <div v-if="item.uploading" class="mt-50">
                <div class="d-flex justify-content-between text-muted small mb-25">
                  <span>Enviando vídeo...</span>
                  <span>{{ item.uploadProgress }}%</span>
                </div>
                <b-progress
                  :value="item.uploadProgress"
                  max="100"
                  height="6px"
                  animated
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Thumbnail / capa (URL)">
              <b-form-input
                v-model="item.thumbnail_url"
                placeholder="https://..."
              />
            </b-form-group>
          </b-col>
          <b-col v-if="itemMediaInvalid(index)" cols="12">
            <small class="text-danger d-block mb-1">
              Envie um vídeo ou informe uma imagem/capa para publicar este item.
            </small>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Descrição breve">
              <b-form-textarea
                v-model="item.description"
                rows="3"
                no-resize
                :maxlength="DESCRIPTION_MAX"
                placeholder="O que mudou, com foco no benefício e uso prático."
                :class="{ 'is-invalid': itemDescriptionInvalid(index) }"
              />
              <div class="invalid-feedback">
                Informe uma descrição breve para publicar.
              </div>
              <div class="d-flex justify-content-end text-muted small mt-25">
                {{ (item.description || "").length }}/{{ DESCRIPTION_MAX }}
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Link complementar (opcional)">
              <b-form-input v-model="item.link" placeholder="https://..." />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <!-- Ações -->
      <b-row>
        <b-col cols="12">
          <b-row class="justify-content-end">
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              :to="{ name: 'whats-new-list' }"
              :disabled="saving"
            >
              Cancelar
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              class="mx-1"
              :disabled="loading || saving || isUploading"
              @click.prevent="save"
            >
              <b-spinner v-if="saving" small />
              <feather-icon v-else icon="SaveIcon" class="mr-50" />
              <span>{{
                isUploading
                  ? "Aguarde o envio do vídeo..."
                  : saving
                  ? "Salvando..."
                  : "Salvar"
              }}</span>
            </b-button>
          </b-row>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormFile,
  BForm,
  BButton,
  BCard,
  BBadge,
  BSpinner,
  BProgress,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as types from "../store/types";
import {
  WHATS_NEW_CATEGORIES,
  WHATS_NEW_STATUS_OPTIONS,
  WHATS_NEW_STATUS,
} from "@/constants/whats_new";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormFile,
    BForm,
    BButton,
    BBadge,
    BSpinner,
    BProgress,
    vSelect,
  },
  directives: { Ripple },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  data() {
    return {
      loading: false,
      saving: false,
      title: undefined,
      version: undefined,
      status: WHATS_NEW_STATUS.DRAFT,
      items: [],
      categories: WHATS_NEW_CATEGORIES,
      statusOptions: WHATS_NEW_STATUS_OPTIONS,
      DESCRIPTION_MAX: 240,
    };
  },
  computed: {
    isUploading() {
      return this.items.some((item) => item.uploading);
    },
    publishing() {
      return this.status === WHATS_NEW_STATUS.PUBLISHED;
    },
  },
  validations() {
    const { publishing } = this;
    return {
      title: { required },
      items: {
        valid: (value) =>
          Array.isArray(value) &&
          value.length > 0 &&
          value.every(
            (item) =>
              !!item.title &&
              (!publishing ||
                ((item.description || "").trim() !== "" &&
                  (!!item.video_url || !!item.thumbnail_url)))
          ),
      },
    };
  },
  mounted() {
    if (this.$route.params.id) {
      this.loading = true;
      this.getRelease(this.$route.params.id)
        .then((response) => {
          const release = response.data;
          this.title = release.title;
          this.version = release.version;
          this.status = release.status;
          this.items = (release.items || []).map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            category: item.category,
            video_url: item.video_url, // key do S3
            video_src: item.video_src, // URL assinada (preview)
            thumbnail_url: item.thumbnail_url,
            link: item.link,
            uploading: false,
            uploadProgress: 0,
            videoFile: null,
            fileKey: 0,
          }));
          this.v$.$touch();
        })
        .catch(() => {
          this.showError(
            "Ocorreu um erro ao carregar o comunicado. Entre em contato com o setor de TI."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    } else {
      this.addItem();
    }
  },
  methods: {
    ...mapActions({
      saveRelease: types.SAVE_RELEASE,
      getRelease: types.GET_RELEASE,
      uploadVideo: types.UPLOAD_VIDEO,
    }),
    addItem() {
      this.items.push({
        id: undefined,
        title: "",
        description: "",
        category: null,
        video_url: null, // key do S3 (preenchido após upload)
        video_src: null, // URL assinada p/ preview (não é enviada)
        thumbnail_url: "",
        link: "",
        uploading: false,
        uploadProgress: 0,
        videoFile: null,
        fileKey: 0,
      });
    },
    // Remonta o b-form-file (via :key) porque zerar só o v-model não limpa o nome exibido.
    resetVideoField(item) {
      item.videoFile = null;
      item.fileKey += 1;
    },
    onVideoSelected(item, file) {
      if (!file) return;
      const MAX_BYTES = 200 * 1024 * 1024;
      if (file.size > MAX_BYTES) {
        this.showError("O vídeo excede o limite de 200MB.");
        this.resetVideoField(item);
        return;
      }
      item.uploading = true;
      item.uploadProgress = 0;
      this.uploadVideo({
        file,
        onProgress: (percent) => {
          item.uploadProgress = percent;
        },
      })
        .then((response) => {
          item.video_url = response.data.video_url; // key do S3
          item.video_src = response.data.video_src; // URL assinada (preview)
        })
        .catch(() => {
          this.showError(
            "Ocorreu um erro ao enviar o vídeo. Tente novamente."
          );
          this.resetVideoField(item);
        })
        .finally(() => {
          item.uploading = false;
        });
    },
    removeItem(index) {
      this.items.splice(index, 1);
    },
    moveItem(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.items.length) return;
      const moved = this.items.splice(index, 1)[0];
      this.items.splice(target, 0, moved);
    },
    itemTitleInvalid(index) {
      return this.v$.items.$error && !this.items[index].title;
    },
    itemDescriptionInvalid(index) {
      return (
        this.v$.items.$error &&
        this.publishing &&
        (this.items[index].description || "").trim() === ""
      );
    },
    itemMediaInvalid(index) {
      return (
        this.v$.items.$error &&
        this.publishing &&
        !this.items[index].video_url &&
        !this.items[index].thumbnail_url
      );
    },
    async save() {
      if (this.isUploading) {
        this.showError(
          "Aguarde o envio do vídeo terminar antes de salvar."
        );
        return;
      }

      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;

      this.saving = true;
      const payload = {
        id: this.$route.params.id,
        title: this.title,
        version: this.version != null && this.version !== "" ? String(this.version) : null,
        status: this.status,
        items: this.items.map((item, index) => ({
          id: item.id,
          order: index,
          title: item.title,
          description: item.description,
          category: item.category,
          video_url: item.video_url,
          thumbnail_url: item.thumbnail_url,
          link: item.link,
        })),
      };
      this.saveRelease(payload)
        .then(() => {
          this.showSuccess(
            `O comunicado foi ${
              this.$route.params.id ? "atualizado" : "criado"
            } com sucesso`
          );
          this.$router.push({ name: "whats-new-list" });
        })
        .catch((err) => {
          this.showError(
            err?.response?.data?.message ||
              "Ocorreu um erro ao salvar o comunicado. Entre em contato com o setor de TI."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    showSuccess(text) {
      this.toast({
        component: ToastificationContent,
        props: { title: "Sucesso", text, icon: "CoffeeIcon", variant: "success" },
      });
    },
    showError(text) {
      this.toast({
        component: ToastificationContent,
        props: { title: "Oops!", text, icon: "AlertTriangleIcon", variant: "danger" },
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.whats-new-video-preview {
  width: 100%;
  max-height: 180px;
  border-radius: 0.4rem;
  background: #000;
}
</style>
